'use client';
import {Box} from '@mui/material';
import {styled} from '@mui/material/styles';

// Most dots rendered at once. Beyond this the row windows around the active
// item so a 20-image gallery doesn't turn the row into a full-width bar.
const MAX_DOTS = 5;

// Instagram-style: dots sit directly on the image, no pill chrome behind them.
// Purely an affordance — `pointerEvents: none` keeps taps falling through to
// the media beneath, and it is hidden from md up where the hover arrows take over.
const DotsRow = styled(Box)(({theme}) => ({
  position: 'absolute',
  insetInline: 0,
  bottom: theme.spacing(1),
  zIndex: 1,
  width: 'fit-content',
  marginInline: 'auto',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.75),
  [theme.breakpoints.up('md')]: {display: 'none'},
}));

// The active item stretches into a short bar; the rest stay circles. Edge dots
// of a windowed row shrink to signal that the sequence continues past them.
const Dot = styled(Box, {shouldForwardProp: propName => propName !== '$active' && propName !== '$edge'})<{
  $active: boolean;
  $edge: boolean;
}>(({theme, $active, $edge}) => ({
  height: 6,
  width: $active ? 18 : 6,
  flexShrink: 0,
  borderRadius: $active ? theme.radii.full : '50%',
  backgroundColor: $active ? theme.palette.colors.surface.neutral.default : theme.palette.colors.surface.lightOverlay,
  transform: $edge && !$active ? 'scale(0.6)' : undefined,
  transition: theme.transitions.create(['width', 'transform']),
}));

export function visibleDotWindow(total: number, activeIndex: number): number[] {
  if (total <= MAX_DOTS) {
    return Array.from({length: total}, (_, index) => index);
  }
  const half = Math.floor(MAX_DOTS / 2);
  const start = Math.min(Math.max(0, activeIndex - half), total - MAX_DOTS);
  return Array.from({length: MAX_DOTS}, (_, offset) => start + offset);
}

export interface GalleryDotsProps {
  total: number;
  activeIndex: number;
}

export function GalleryDots({total, activeIndex}: GalleryDotsProps) {
  if (total < 2) {
    return null;
  }

  const indices = visibleDotWindow(total, activeIndex);
  const windowed = indices.length < total;

  return (
    <DotsRow aria-hidden>
      {indices.map((index, position) => (
        <Dot
          key={index}
          $active={index === activeIndex}
          $edge={windowed && (position === 0 ? index > 0 : position === indices.length - 1 && index < total - 1)}
        />
      ))}
    </DotsRow>
  );
}

GalleryDots.displayName = 'GalleryDots';
